/**
 * Traduction des erreurs Supabase en messages localisés (clés i18n
 * `common.supabaseErrors.*`, voir i18n/locales/fr.json et en.json).
 */

import i18n from "@/i18n";

export interface SupabaseErrorTranslation {
  title: string;
  message: string;
}

/** Construit le couple titre/message depuis une clé `common.supabaseErrors.<key>`. */
function errorFromKey(key: string): SupabaseErrorTranslation {
  return {
    title: i18n.t(`common.supabaseErrors.${key}.title`),
    message: i18n.t(`common.supabaseErrors.${key}.message`),
  };
}

/**
 * Traduit les erreurs Supabase courantes en messages chaleureux et professionnels
 */
export function translateSupabaseError(error: any): SupabaseErrorTranslation {
  const errorMessage = error?.message || "";
  const errorCode = error?.code || "";

  // Erreurs d'authentification
  if (errorMessage === "Invalid login credentials") {
    return errorFromKey("invalidCredentials");
  }

  if (errorMessage.includes("Email not confirmed")) {
    return errorFromKey("emailNotConfirmed");
  }

  if (errorMessage.includes("User already registered")) {
    return errorFromKey("userAlreadyRegistered");
  }

  // Mot de passe présent dans une fuite de données connue (HaveIBeenPwned,
  // activé côté Supabase). CAS DISTINCT du format : « Abcdefgh1 » respecte les
  // 4 règles affichées par PasswordValidator et se faisait pourtant refuser
  // avec le message « il doit contenir 8 caractères, une majuscule… » — message
  // faux, l'utilisateur ne pouvait pas comprendre et tournait en rond.
  // supabase-js expose `reasons` sur AuthWeakPasswordError ; le repli sur le
  // texte couvre le cas où l'erreur arrive sans cette propriété.
  const pwnedReason =
    Array.isArray(error?.reasons) && error.reasons.includes("pwned");
  if (
    pwnedReason ||
    errorMessage.includes("known to be weak") ||
    errorMessage.includes("easy to guess")
  ) {
    return errorFromKey("pwnedPassword");
  }

  // Format insuffisant. « should be at least » est le libellé réel de GoTrue
  // pour la longueur minimale — il n'était couvert par aucun des motifs.
  if (
    (errorMessage.includes("Password") && errorMessage.includes("weak")) ||
    errorMessage.includes("too short") ||
    errorMessage.includes("should be at least")
  ) {
    return errorFromKey("weakPassword");
  }

  if (errorMessage.includes("Email rate limit exceeded")) {
    return errorFromKey("rateLimit");
  }

  if (errorMessage.includes("Invalid email")) {
    return errorFromKey("invalidEmail");
  }

  if (errorMessage.includes("User not found")) {
    return errorFromKey("userNotFound");
  }

  if (errorMessage.includes("Token has expired") || errorMessage.includes("expired")) {
    return errorFromKey("linkExpired");
  }

  if (errorMessage.includes("Network") || errorMessage.includes("network") || errorMessage.includes("fetch")) {
    return errorFromKey("network");
  }

  if (errorMessage.includes("Database error") || errorCode === "PGRST116" || errorCode === "23505") {
    // 23505 = unique_violation Postgres
    return errorFromKey("accountExists");
  }

  if (
    errorMessage.includes("violates check constraint") ||
    errorMessage.includes("check_constraint") ||
    errorCode === "23514"
  ) {
    return errorFromKey("invalidData");
  }

  if (
    errorMessage.includes("violates row-level security") ||
    errorMessage.includes("permission denied") ||
    errorCode === "42501"
  ) {
    return errorFromKey("accessDenied");
  }

  if (errorMessage.includes("JWT") || errorMessage.includes("token") || errorMessage.includes("expired")) {
    return errorFromKey("sessionExpired");
  }

  if (errorMessage.includes("signup") || errorMessage.includes("Signup")) {
    return errorFromKey("signupFailed");
  }

  // Aucun cas connu — message générique (on n'expose JAMAIS le message
  // brut Supabase qui peut être en anglais ou révéler des détails internes).
  return errorFromKey("unknown");
}

/**
 * Traduit les erreurs de validation de profil
 */
export function translateProfileError(error: any): SupabaseErrorTranslation {
  const errorMessage = error?.message || "";

  if (errorMessage.includes("duplicate") || errorMessage.includes("unique")) {
    return errorFromKey("profileDuplicate");
  }

  return translateSupabaseError(error);
}
